// Debounced "set this state" sends, shared by the actions that need one (post like, comment like).
//
// The action updates the screen at once, then calls debounceSettle(): only the state the user ended on is sent, once the clicks
// on that key stop for DEBOUNCE_MS, and nothing is sent when it ends where it started. A failed send puts the screen
// back (revert). Pending entries live in memory only; app.jsx calls debounceFlush() when the page is hidden or closed so
// they go out at once (keepalive) instead of being lost with the tab.
const DEBOUNCE_MS = 3000;

const pending = new Map(); // key -> { timer, base, revert, want, send }

const keepaliveOptions = { adapter: 'fetch', fetchOptions: { keepalive: true } };

function run(key, keepalive) {
    const p = pending.get(key);
    if (!p) return;
    pending.delete(key);
    clearTimeout(p.timer);
    if (p.want === p.base) return; // ended where it started: the server already has that
    // send(want, axiosOptions) resolves false (or rejects) when it failed
    Promise.resolve(p.send(p.want, keepalive ? keepaliveOptions : undefined))
        .then((ok) => ok === false && p.revert())
        .catch(() => p.revert());
}

// key     one entry per target, e.g. `like:${postId}`
// base    the state before the first click (kept from the first call; later calls cannot change it)
// want    the state the user is on now
// send    (want, axiosOptions) => Promise<boolean>; the latest call's one is used
// revert  puts the screen back to `base` (kept from the first call, so it restores the state before the clicks)
function debounceSettle(key, { base, want, send, revert, delay = DEBOUNCE_MS }) {
    let p = pending.get(key);
    if (!p) pending.set(key, (p = { base, revert }));
    clearTimeout(p.timer);
    p.want = want;
    p.send = send;
    p.timer = setTimeout(() => run(key, false), delay);
}

// Send everything still pending now (page hidden / closed). Entries already sent are gone, so nothing goes twice.
function debounceFlush() {
    for (const key of [...pending.keys()]) run(key, true);
}

export { debounceSettle, debounceFlush };
